import { Suspense } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import * as Collapsible from '@radix-ui/react-collapsible';
import { updateTransaction } from 'api/transactions';
import { TransactionFundInput } from 'assets/js/pages/transactions/TransactionForm';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface FundForm {
    selectedFund: string[];
}

const TransactionEditableFund = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const { data: transaction } = useCurrentTransactionOrById({ refetchOnMount: false });

    const methods = Form.useForm<FundForm>({
        defaultValues: {
            selectedFund: transaction?.fund ? [transaction.fund.id.toString()] : [],
        },
    });

    const { path, handleSubmit, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: FundForm) =>
            updateTransaction(transaction?.id!, {
                fund_id: values.selectedFund.length > 0 ? parseInt(values.selectedFund[0]) : null,
            }),
        onSuccess: response => {
            toast.success('Successfully updated transaction fund!');

            queryClient.setQueryData(
                transactionQueryKeys.transaction(accountId, transaction.id),
                (oldData: Transaction) => ({
                    ...oldData,
                    fund: response.fund,
                }),
            );
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Failed to update transaction fund');
            resetFormButton();
        },
    });

    return (
        <Form.Provider key={key} {...methods}>
            <form className={'contents'} onSubmit={handleSubmit(submit as SubmitHandler<FundForm>)}>
                <div className={'flex flex-col'}>
                    <Form.Label name={path('selectedFund')} className={'text-gray-800'}>
                        Fund
                    </Form.Label>
                    <EditableInput.Root>
                        <EditableInput.ReadOnly>
                            <div className={'flex items-center gap-2'}>
                                {transaction?.fund?.name ? (
                                    <>
                                        <p className={'text-gray-600'}>{transaction.fund.name}</p>
                                        <EditableInput.ReadOnlyEditTrigger />
                                    </>
                                ) : (
                                    <Collapsible.Trigger className={'font-medium text-blue-500'}>
                                        Credit a fund +
                                    </Collapsible.Trigger>
                                )}
                            </div>
                        </EditableInput.ReadOnly>
                        <EditableInput.Content>
                            <Suspense fallback={null}>
                                <Form.Container>
                                    <TransactionFundInput />
                                </Form.Container>
                                <EditableInput.ActionsContainer>
                                    <EditableInput.Cancel />
                                    <Form.SubmitButton>Save</Form.SubmitButton>
                                </EditableInput.ActionsContainer>
                            </Suspense>
                        </EditableInput.Content>
                    </EditableInput.Root>
                </div>
            </form>
        </Form.Provider>
    );
};

export { TransactionEditableFund };
